import React, { useState } from 'react';
import { useSnackbar } from 'notistack';

import {
    IconButton,
    Switch,
    Tooltip,
    CircularProgress,
    Box,
} from '@material-ui/core';
import DownloadIcon from '@material-ui/icons/GetApp';
import PlayIcon from '@material-ui/icons/PlayCircleOutline';
import { default as PlayerSettingsIcon } from '@material-ui/icons/Settings';
import { default as GridIcon } from '@material-ui/icons/GridOn';

import { usePrivileges } from '../../../../../../../../../state/auth/index.hooks';

import TranslationHelper from '../../../../../../../../../helpers/TranslationHelper';

import { LABELS_MODEL_NOT_SELECTED } from '../../../../../../../../../constants/videoPlayerSettings';

import { usePreviewAction } from '../../../../../../../selectors/index.hooks';
import { showPlayerSettingsDialog } from '../../../../../../../../..//state/ui/dialog';
import { useAppDispatch } from '../../../../../../../../../hooks';
import { IPlayerSettings } from '../../../../../../../../../state/ui/discoverySlice';
import downloadMovie from '../../../../../../../../../helpers/moviePlayerOptions/downloadMovie';

interface IOwnProps {
    getMovieDownloadUrl: (paramName: string) => string;
    setShowVideoPlayerActions: (bool: boolean) => void;
    showPlayer: boolean;
    handlePlayerOpen: () => void;
    handleOpenDetails: (anonymize: boolean) => void;
    playerSettings: IPlayerSettings | null;
    handleChangePlayerSettings: (settings: IPlayerSettings) => void;
    classes: any;
    paramName: string;
}

type TProps = IOwnProps;

const PlayerActionsContainer = ({
    classes,
    getMovieDownloadUrl,
    setShowVideoPlayerActions,
    showPlayer,
    handlePlayerOpen,
    handleOpenDetails,
    playerSettings,
    handleChangePlayerSettings,
    paramName,
}: TProps) => {
    const [downloadingStart, setDownloadingStart] = useState(false);
    const privileges = usePrivileges();
    const previewAction = usePreviewAction();
    const { enqueueSnackbar } = useSnackbar();
    const dispatch = useAppDispatch();
    const anonymize = playerSettings?.anonymize ?? privileges.anonymize;
    const handleChange = (playerSettings: IPlayerSettings) => {
        handleChangePlayerSettings({
            labels:
                playerSettings.model === LABELS_MODEL_NOT_SELECTED
                    ? undefined
                    : true,
            model:
                playerSettings.model === LABELS_MODEL_NOT_SELECTED
                    ? undefined
                    : playerSettings.model,
            confidenceThreshold: !playerSettings.confidenceThreshold
                ? undefined
                : playerSettings.confidenceThreshold,
            anonymize: playerSettings.anonymize,
        });
    };

    const showNotification = (success: boolean, message: string) => {
        enqueueSnackbar(message, {
            variant: success ? 'success' : 'error',
        });
    };

    const handleDownloadFile = async () => {
        setDownloadingStart(true);
        downloadMovie({
            url: getMovieDownloadUrl(paramName.toLowerCase()),
            onSuccess: () => {
                setDownloadingStart(false);
                setShowVideoPlayerActions(false);
            },
            onError: () => {
                setDownloadingStart(false);
                setShowVideoPlayerActions(false);
                showNotification(
                    false,
                    TranslationHelper.translate(
                        'An error has occurred, please try again later'
                    )
                );
            },
        });
    };

    return (
        <div className={classes.actionsContainer}>
            <>
                {downloadingStart ? (
                    <Box sx={{ display: 'flex' }}>
                        <CircularProgress
                            aria-label="download-circular-progress"
                            size={20}
                            className={classes.downloadCircularProgress}
                        />
                    </Box>
                ) : (
                    <Tooltip title={TranslationHelper.translate('Download')}>
                        <IconButton
                            className={
                                showPlayer ? classes.buttonPlay : classes.button
                            }
                            onClick={handleDownloadFile}
                        >
                            <DownloadIcon />
                        </IconButton>
                    </Tooltip>
                )}
            </>
            {showPlayer && (
                <Tooltip title={`${TranslationHelper.translate('Play')}`}>
                    <IconButton
                        className={classes.buttonPlay}
                        onClick={handlePlayerOpen}
                    >
                        <PlayIcon />
                    </IconButton>
                </Tooltip>
            )}
            {!previewAction?._webx?.changeSourceSetDisabled && (
                <Tooltip title={TranslationHelper.translate('Show details')}>
                    <IconButton
                        className={
                            showPlayer ? classes.buttonPlay : classes.button
                        }
                        onClick={() => handleOpenDetails(anonymize)}
                    >
                        <GridIcon />
                    </IconButton>
                </Tooltip>
            )}
            {privileges.anonymizeSwitchVisible && (
                <>
                    <Tooltip title={TranslationHelper.translate('Anonymize')}>
                        <Switch
                            classes={{
                                track: classes.switchTrack,
                                colorPrimary: classes.switchColorPrimary,
                                switchBase: classes.switchBase,
                            }}
                            color="primary"
                            onChange={(e, checked) =>
                                handleChangePlayerSettings({
                                    ...playerSettings,
                                    anonymize: checked,
                                })
                            }
                            checked={anonymize}
                        />
                    </Tooltip>
                </>
            )}

            {privileges.videoProcessingDebug && (
                <Tooltip
                    title={TranslationHelper.translate('Show player settings')}
                >
                    <IconButton
                        className={classes.buttonPlay}
                        onClick={() =>
                            dispatch(
                                showPlayerSettingsDialog({
                                    handleChange,
                                    playerSettings: {
                                        ...playerSettings,
                                        anonymize,
                                    },
                                })
                            )
                        }
                    >
                        <PlayerSettingsIcon />
                    </IconButton>
                </Tooltip>
            )}
        </div>
    );
};

export default PlayerActionsContainer;
